import React, { useMemo, useEffect, useState } from 'react';
import { ItineraryItem, Location } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';
import { MapPinIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

interface SafariRouteMapProps {
    items: ItineraryItem[];
    className?: string;
    isSimulating?: boolean;
    onSimulationComplete?: () => void;
}

const SafariRouteMap: React.FC<SafariRouteMapProps> = ({
    items,
    className = '',
    isSimulating = false,
    onSimulationComplete
}) => {
    const { actualTheme } = useTheme();
    const [progress, setProgress] = useState(0);

    // Simulation Loop
    useEffect(() => {
        if (isSimulating) {
            setProgress(0);
            const duration = 15000; // 15 seconds for full route
            const interval = 16; // 60fps
            const step = 100 / (duration / interval);

            const timer = setInterval(() => {
                setProgress(prev => {
                    const next = prev + step;
                    if (next >= 100) {
                        clearInterval(timer);
                        return 100;
                    }
                    return next;
                });
            }, interval);
            return () => clearInterval(timer);
        } else {
            setProgress(0);
        }
    }, [isSimulating, onSimulationComplete]);

    // Separate effect for completion callback to avoid "setState in render" warning
    useEffect(() => {
        if (progress >= 100 && isSimulating && onSimulationComplete) {
            onSimulationComplete();
        }
    }, [progress, isSimulating, onSimulationComplete]);

    // Filter items with valid locations
    const locations = useMemo(() =>
        items.filter(item => item.location && item.location.coordinates),
        [items]);

    // Calculate Bounding Box and Normalization
    const { viewBox, points, totalDistance } = useMemo(() => {
        if (locations.length === 0) return { viewBox: '0 0 1000 1000', points: [], totalDistance: 0 };

        const lats = locations.map(l => l.location!.coordinates.lat);
        const lngs = locations.map(l => l.location!.coordinates.lng);

        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);

        const padding = 0.2;
        const latRange = (maxLat - minLat) || 0.1;
        const lngRange = (maxLng - minLng) || 0.1;

        const vMinLat = minLat - latRange * padding;
        const vMaxLat = maxLat + latRange * padding;
        const vMinLng = minLng - lngRange * padding;
        const vMaxLng = maxLng + lngRange * padding;

        const normalize = (lat: number, lng: number) => {
            const y = 1000 - ((lat - vMinLat) / (vMaxLat - vMinLat)) * 1000;
            const x = ((lng - vMinLng) / (vMaxLng - vMinLng)) * 1000;
            return { x, y };
        };

        const pts = locations.map(l => ({
            ...l,
            point: normalize(l.location!.coordinates.lat, l.location!.coordinates.lng)
        }));

        // Estimate total distance (very rough, just for UI)
        let dist = 0;
        for (let i = 0; i < locations.length - 1; i++) {
            dist += Math.sqrt(
                Math.pow(locations[i].location!.coordinates.lat - locations[i + 1].location!.coordinates.lat, 2) +
                Math.pow(locations[i].location!.coordinates.lng - locations[i + 1].location!.coordinates.lng, 2)
            ) * 111; // ~111km per degree
        }

        return {
            viewBox: '0 0 1000 1000',
            points: pts,
            totalDistance: Math.round(dist)
        };
    }, [locations]);

    // Path Generation
    const pathData = useMemo(() => {
        if (points.length < 2) return '';
        // Create a smooth curve or straight lines
        // Google Maps uses Polyline, here we use connected lines
        return points.map((p, i) =>
            `${i === 0 ? 'M' : 'L'} ${p.point.x} ${p.point.y}`
        ).join(' ');
    }, [points]);

    // Calculate current position on path based on progress
    const currentPos = useMemo(() => {
        if (points.length < 2) return { x: 0, y: 0, heading: 0 };
        const totalSegments = points.length - 1;
        const segmentProgress = (progress / 100) * totalSegments;
        const currentSegmentIndex = Math.min(Math.floor(segmentProgress), totalSegments - 1);
        const segmentPercent = segmentProgress - currentSegmentIndex;

        const p1 = points[currentSegmentIndex].point;
        const p2 = points[currentSegmentIndex + 1].point;

        return {
            x: p1.x + (p2.x - p1.x) * segmentPercent,
            y: p1.y + (p2.y - p1.y) * segmentPercent,
            heading: Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI
        };
    }, [progress, points]);

    if (locations.length < 2) {
        return (
            <div className={`flex items-center justify-center h-64 rounded-xl border-2 border-dashed ${actualTheme === 'dark' ? 'border-slate-700 text-slate-500' : 'border-neutral-300 text-neutral-400'
                } ${className}`}>
                <p>Add tours to generate route map.</p>
            </div>
        );
    }

    return (
        <div className={`relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl border-4 ${actualTheme === 'dark' ? 'border-slate-800 bg-[#e5e7eb]' : 'border-white bg-[#e5e7eb]'
            } ${className}`}>

            {/* Map Texture Background (Faux Google Maps Style) */}
            <div className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
            />

            {/* Terrain Blobs (Decorative) */}
            <div className="absolute top-0 right-0 w-full h-full opacity-30 mix-blend-multiply pointer-events-none">
                <div className="absolute top-10 left-10 w-40 h-40 bg-green-200 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-20 w-60 h-60 bg-green-300 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-yellow-100/50 rounded-full blur-3xl"></div>
            </div>

            {/* Navigation Header Overlay */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-start gap-2">
                <div className="flex-1 bg-green-700 p-4 rounded-lg shadow-lg text-white flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-sm opacity-90 mb-1">
                            <ArrowRightIcon className="h-4 w-4" />
                            <span>Using AI Route Optimization</span>
                        </div>
                        <div className="text-xl font-bold leading-tight">
                            {progress < 100
                                ? `En route to ${points[Math.min(Math.floor((progress / 100) * (points.length - 1)) + 1, points.length - 1)]?.location?.name || 'Next Stop'}`
                                : 'Safari Completed'
                            }
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold">{(totalDistance * (1 - progress / 100)).toFixed(0)} km</div>
                        <div className="text-xs opacity-80">remaining</div>
                    </div>
                </div>
                <div className="bg-white p-3 rounded-full shadow-lg text-neutral-800">
                    <MapPinIcon className="h-6 w-6 text-red-600" />
                </div>
            </div>

            {/* SVG Map Layer */}
            <svg
                viewBox={viewBox}
                className="w-full h-full relative z-0"
                style={{ transform: 'scale(1.1)' }} // Zoom in slightly
            >
                {/* Roads (White outlines) */}
                <path
                    d={pathData}
                    fill="none"
                    stroke="white"
                    strokeWidth="16"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-90"
                />

                {/* Base Route (Blue - Google Maps Style) */}
                <path
                    id="base-route"
                    d={pathData}
                    fill="none"
                    stroke="#4285F4"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Traveled Route (Red - User Request) */}
                {/* We use a mask or simpler: re-draw path with 'pathLength' and dashoffset */}
                <path
                    d={pathData}
                    fill="none"
                    stroke="#EF4444" // Red
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pathLength="100"
                    strokeDasharray="100"
                    strokeDashoffset={100 - progress}
                />

                {/* Destination Markers */}
                {points.map((p, i) => (
                    <g key={i}>
                        {/* Shadow */}
                        <circle cx={p.point.x} cy={p.point.y + 5} r="15" fill="black" opacity="0.2" />

                        {/* Marker Pin */}
                        <circle
                            cx={p.point.x}
                            cy={p.point.y}
                            r="12"
                            fill={i === 0 ? '#10B981' : i === points.length - 1 ? '#EF4444' : '#fff'}
                            stroke="#333"
                            strokeWidth="2"
                        />
                        <text
                            x={p.point.x}
                            y={p.point.y}
                            dy="4"
                            textAnchor="middle"
                            fontSize="10"
                            fontWeight="bold"
                            fill={i === 0 || i === points.length - 1 ? 'white' : '#333'}
                        >
                            {p.day}
                        </text>
                    </g>
                ))}

                {/* Valid Simulation State Check */}
                {isSimulating && points.length >= 2 && (
                    <g transform={`translate(${currentPos.x}, ${currentPos.y})`}>
                        {/* Pulse */}
                        <circle r="40" fill="#4285F4" className="animate-ping opacity-30" />

                        {/* Vehicle/Cursor Arrow */}
                        <path
                            d="M0,-15 L10,10 L0,5 L-10,10 Z"
                            fill="#4285F4"
                            stroke="white"
                            strokeWidth="2"
                            transform={`rotate(${currentPos.heading + 90})`}
                            style={{ filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.3))' }}
                        />
                    </g>
                )}
            </svg>

            {/* Google Maps Bottom Status Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-[#202124] border-t border-white/10 flex items-center justify-between px-6 z-10 shadow-[0_-4px_10px_rgba(0,0,0,0.3)]">
                <div className="flex items-center gap-4">
                    <button className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all text-white">
                        <span className="text-xl font-light">✕</span>
                    </button>
                    <div className="w-[1px] h-8 bg-white/20 ml-2" />
                </div>

                <div className="flex flex-col items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-[#FBBC04] text-xl font-bold">
                            {Math.floor((totalDistance * (1 - progress / 100)) / 50)} hr {Math.round(((totalDistance * (1 - progress / 100)) % 50) / 50 * 60)} min
                        </span>
                        <div className="flex items-center gap-1.5 opacity-80 text-white/70">
                            <span className="w-4 h-4 rounded-full border border-white/40 flex items-center justify-center text-[10px]">⚖️</span>
                            <span className="w-4 h-4 rounded-full border border-white/40 flex items-center justify-center text-[10px]">☁️</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-white/50 text-xs mt-0.5">
                        <span>{(totalDistance * (1 - progress / 100)).toFixed(0)} km</span>
                        <span>•</span>
                        <span>{new Date(Date.now() + (totalDistance * (1 - progress / 100) / 50 * 3600000)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="w-[1px] h-8 bg-white/20" />
                    <button className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center hover:bg-blue-500 transition-all text-white shadow-lg">
                        <svg className="w-6 h-6 rotate-45" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71L12 2z" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="absolute bottom-20 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-md text-xs font-bold text-gray-600 shadow-sm">
                SightTrails Navigation
            </div>
        </div>
    );
};

export default SafariRouteMap;
